import { useEffect, useRef, useState } from 'react';
import { Alert, AspectRatio, Box, Text } from '@mantine/core';

/**
 * Plays a (signed) HLS URL with hls.js (loaded on demand), or natively where supported
 * (Safari). Never autoplays.
 */
export function HlsPlayer({ src, title }: { src: string; title: string }) {
  const ref = useRef<HTMLVideoElement>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const video = ref.current;
    if (!video) return;
    setError(null);
    let cancelled = false;
    let destroy: (() => void) | undefined;

    void import('hls.js').then(({ default: Hls }) => {
      if (cancelled) return;
      if (Hls.isSupported()) {
        const hls = new Hls({ enableWorker: true });
        hls.on(Hls.Events.ERROR, (_e, data) => {
          if (data.fatal)
            setError(
              `Playback failed (${data.details}). The signed link may have expired — reload the preview.`,
            );
        });
        hls.loadSource(src);
        hls.attachMedia(video);
        destroy = () => hls.destroy();
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        video.src = src;
        destroy = () => {
          video.removeAttribute('src');
          video.load();
        };
      } else {
        setError('This browser can’t play HLS video.');
      }
    });

    return () => {
      cancelled = true;
      destroy?.();
    };
  }, [src]);

  return (
    <Box>
      <AspectRatio ratio={16 / 9} maw={720}>
        <video
          ref={ref}
          controls
          playsInline
          aria-label={`Video: ${title}`}
          style={{ background: '#000', borderRadius: 14, border: '2px solid var(--pb-outline)' }}
        />
      </AspectRatio>
      {error ? (
        <Alert color="coral" variant="light" mt="xs">
          {error}
        </Alert>
      ) : (
        <Text size="xs" c="dimmed" mt={4}>
          {title}
        </Text>
      )}
    </Box>
  );
}
